import type { Site } from '@shared/types'

export interface Fix {
  lat: number
  lng: number
  accuracy: number
  at: number
}

export function haversineMeters(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const toRad = Math.PI / 180
  const dLat = (bLat - aLat) * toRad
  const dLng = (bLng - aLng) * toRad
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(aLat * toRad) * Math.cos(bLat * toRad)
  return 2 * 6_371_008.8 * Math.asin(Math.min(1, Math.sqrt(h)))
}

export function nearestSite(sites: Site[], fix: Fix): { site: Site; distanceM: number } | null {
  let best: { site: Site; distanceM: number } | null = null
  for (const site of sites) {
    if (site.lat == null || site.lng == null) continue
    const distanceM = haversineMeters(fix.lat, fix.lng, site.lat, site.lng)
    if (!best || distanceM < best.distanceM) best = { site, distanceM }
  }
  return best
}

/**
 * Whether this session has been allowed to read the location.
 *
 * iPhone home-screen apps forget the location permission between launches, so
 * starting the site watcher the moment the app opens put the "Allow
 * location?" dialog in front of people every single time. The watcher now only
 * starts on open when the browser says permission is already granted; anywhere
 * else it waits for the first location the person asked for themselves
 * (clocking in, posting an update, the site picker), and starts from there.
 */
let granted = false
const grantListeners = new Set<() => void>()

export function markLocationGranted(): void {
  if (granted) return
  granted = true
  for (const listener of grantListeners) listener()
}

export function onLocationGranted(listener: () => void): () => void {
  if (granted) listener()
  grantListeners.add(listener)
  return () => grantListeners.delete(listener)
}

/** 'unknown' where the Permissions API is missing or refuses the query. */
export async function locationPermission(): Promise<PermissionState | 'unknown'> {
  try {
    if (!navigator.permissions?.query) return 'unknown'
    const status = await navigator.permissions.query({ name: 'geolocation' })
    return status.state
  } catch {
    return 'unknown'
  }
}

export function getCurrentFix(options: PositionOptions = {}): Promise<Fix> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('This device cannot report its location'))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        markLocationGranted()
        resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        at: pos.timestamp,
        })
      },
      (err) => reject(new Error(geolocationMessage(err))),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000, ...options },
    )
  })
}

export function geolocationMessage(err: GeolocationPositionError): string {
  switch (err.code) {
    case err.PERMISSION_DENIED:
      return 'Location is turned off for this app. Turn it on to stamp your clock-ins.'
    case err.POSITION_UNAVAILABLE:
      return 'No location fix yet. Step outside or try again in a moment.'
    case err.TIMEOUT:
      return 'Getting your location took too long.'
    default:
      return 'Could not read your location.'
  }
}

export type SiteWatchEvent =
  | { type: 'arrived'; site: Site; fix: Fix }
  | { type: 'departed'; site: Site | null; fix: Fix }
  | { type: 'fix'; fix: Fix; insideSite: Site | null }
  | { type: 'error'; message: string }

export interface SiteWatchOptions {
  minAccuracyM: number
  /** How long a new geofence must hold before it counts as a real arrival. */
  dwellSeconds: number
}

/**
 * Watches the phone's position against the site geofences and reports
 * confirmed arrivals and departures.
 *
 * The dwell requirement is the important part: a GPS fix bouncing 200 m across
 * a parking lot must not fire "you have moved sites" at someone holding a nail
 * gun. A candidate site has to hold continuously for the dwell period, and
 * low-accuracy fixes are discarded outright rather than averaged in.
 */
export class SiteWatcher {
  private watchId: number | null = null
  private sites: Site[] = []
  private options: SiteWatchOptions
  private listener: (event: SiteWatchEvent) => void

  /** The geofence we currently believe we are inside (confirmed). */
  private confirmedSiteId: string | null = null
  /** The geofence we are waiting on, and since when. */
  private candidateSiteId: string | null = null
  private candidateSince = 0

  constructor(options: SiteWatchOptions, listener: (event: SiteWatchEvent) => void) {
    this.options = options
    this.listener = listener
  }

  setSites(sites: Site[]): void {
    this.sites = sites.filter((s) => s.lat != null && s.lng != null)
  }

  setOptions(options: Partial<SiteWatchOptions>): void {
    this.options = { ...this.options, ...options }
  }

  /** Seed the confirmed site so an existing clock-in does not re-fire on boot. */
  primeConfirmedSite(siteId: string | null): void {
    this.confirmedSiteId = siteId
    this.candidateSiteId = null
  }

  start(): void {
    if (this.watchId !== null || !('geolocation' in navigator)) return
    this.watchId = navigator.geolocation.watchPosition(
      (pos) => this.handle({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        at: pos.timestamp,
      }),
      (err) => this.listener({ type: 'error', message: geolocationMessage(err) }),
      // Standard accuracy is plenty for a 150 m fence and costs far less
      // battery than high accuracy across a ten hour shift.
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 60_000 },
    )
  }

  stop(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId)
      this.watchId = null
    }
  }

  private handle(fix: Fix): void {
    if (fix.accuracy > this.options.minAccuracyM) return

    const nearest = nearestSite(this.sites, fix)
    const inside = nearest && nearest.distanceM <= nearest.site.radiusM ? nearest.site : null
    const insideId = inside?.id ?? null

    this.listener({ type: 'fix', fix, insideSite: inside })

    if (insideId === this.confirmedSiteId) {
      this.candidateSiteId = null
      return
    }

    if (insideId !== this.candidateSiteId) {
      this.candidateSiteId = insideId
      this.candidateSince = fix.at
      return
    }

    const heldMs = fix.at - this.candidateSince
    if (heldMs < this.options.dwellSeconds * 1000) return

    const previous = this.sites.find((s) => s.id === this.confirmedSiteId) ?? null
    this.confirmedSiteId = insideId
    this.candidateSiteId = null

    if (inside) {
      this.listener({ type: 'arrived', site: inside, fix })
    } else {
      this.listener({ type: 'departed', site: previous, fix })
    }
  }
}
